import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Calendar } from './ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { X } from 'lucide-react';

// Time slot component for flexible time selection
export const TimeSlotSelector = ({ onTimeSlotChange, selectedSlots = [] }) => {
  const [startHour, setStartHour] = useState('09');
  const [startMinute, setStartMinute] = useState('00');
  const [endHour, setEndHour] = useState('17');
  const [endMinute, setEndMinute] = useState('00');

  // Generate hours (00-23)
  const hours = Array.from({ length: 24 }, (_, i) => 
    i.toString().padStart(2, '0')
  );

  // Generate minutes (00, 15, 30, 45)
  const minutes = ['00', '15', '30', '45'];

  const addTimeSlot = () => {
    const startTime = `${startHour}:${startMinute}`;
    const endTime = `${endHour}:${endMinute}`;
    
    // Validate times
    const startMinutes = parseInt(startHour) * 60 + parseInt(startMinute);
    const endMinutes = parseInt(endHour) * 60 + parseInt(endMinute);
    
    if (endMinutes <= startMinutes) {
      alert('End time must be after start time');
      return;
    }

    const newSlot = {
      id: Date.now(),
      start: startTime,
      end: endTime,
      label: `${startTime} - ${endTime}`
    };

    if (!selectedSlots.find(slot => slot.start === startTime && slot.end === endTime)) {
      onTimeSlotChange([...selectedSlots, newSlot]);
    }
  };

  const removeTimeSlot = (id) => {
    onTimeSlotChange(selectedSlots.filter(slot => slot.id !== id));
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-2 items-end">
        <div>
          <Label className="text-xs">Start Hour</Label>
          <Select value={startHour} onValueChange={setStartHour}>
            <SelectTrigger className="h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {hours.map(hour => (
                <SelectItem key={hour} value={hour}>{hour}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label className="text-xs">Start Min</Label>
          <Select value={startMinute} onValueChange={setStartMinute}>
            <SelectTrigger className="h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {minutes.map(minute => (
                <SelectItem key={minute} value={minute}>{minute}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label className="text-xs">End Hour</Label>
          <Select value={endHour} onValueChange={setEndHour}>
            <SelectTrigger className="h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {hours.map(hour => (
                <SelectItem key={hour} value={hour}>{hour}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label className="text-xs">End Min</Label>
          <Select value={endMinute} onValueChange={setEndMinute}>
            <SelectTrigger className="h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {minutes.map(minute => (
                <SelectItem key={minute} value={minute}>{minute}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Button 
        onClick={addTimeSlot} 
        size="sm" 
        className="w-full"
        data-testid="add-time-slot-btn"
      >
        Add Time Slot
      </Button>

      {/* Selected time slots */}
      {selectedSlots.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">Selected Time Slots:</Label>
          <div className="flex flex-wrap gap-2">
            {selectedSlots.map(slot => (
              <Badge 
                key={slot.id} 
                variant="secondary" 
                className="flex items-center gap-1 px-2 py-1"
              >
                <span className="text-xs">{slot.label}</span>
                <X 
                  className="w-3 h-3 cursor-pointer hover:text-red-500" 
                  onClick={() => removeTimeSlot(slot.id)}
                />
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Enhanced Availability Manager Component
export const EnhancedAvailabilityManager = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dailySlots, setDailySlots] = useState({});
  const [currentDaySlots, setCurrentDaySlots] = useState([]);

  const selectedDateStr = selectedDate?.toISOString().split('T')[0];

  useEffect(() => {
    // Load slots for selected date
    setCurrentDaySlots(dailySlots[selectedDateStr] || []);
  }, [selectedDate, dailySlots, selectedDateStr]);

  const handleTimeSlotsChange = (newSlots) => {
    setCurrentDaySlots(newSlots);
    
    // Update daily slots
    const updatedDailySlots = { ...dailySlots };
    if (newSlots.length > 0) {
      updatedDailySlots[selectedDateStr] = newSlots;
    } else {
      delete updatedDailySlots[selectedDateStr];
    }
    setDailySlots(updatedDailySlots);
  };

  const saveAvailability = async () => {
    try {
      // Convert to the format expected by backend
      const availability = {};
      Object.keys(dailySlots).forEach(date => {
        availability[date] = dailySlots[date].map(slot => slot.label);
      });

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/specialist/availability`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(availability)
      });

      if (response.ok) {
        alert('Availability updated successfully!');
      } else {
        throw new Error('Failed to update availability');
      }
    } catch (error) {
      console.error('Error saving availability:', error);
      alert('Failed to save availability. Please try again.');
    }
  };

  const clearDay = () => {
    handleTimeSlotsChange([]);
  };

  // Get total days with availability
  const totalAvailableDays = Object.keys(dailySlots).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Flexible Availability Manager</span>
          <Badge variant="outline">
            {totalAvailableDays} days configured
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-6">
          {/* Calendar */}
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Select Date</Label>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                className="rounded-md border mt-2"
                data-testid="availability-calendar"
              />
            </div>
          </div>
          
          {/* Time Slots Manager */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">
                Time Slots for {selectedDate?.toDateString()}
              </Label>
              {currentDaySlots.length > 0 && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={clearDay}
                  className="text-red-600 hover:text-red-700"
                >
                  Clear Day
                </Button>
              )}
            </div>
            
            <TimeSlotSelector 
              selectedSlots={currentDaySlots}
              onTimeSlotChange={handleTimeSlotsChange}
            />
            
            <div className="pt-4 border-t">
              <Button 
                onClick={saveAvailability}
                className="w-full"
                disabled={totalAvailableDays === 0}
                data-testid="save-availability"
              >
                Save All Availability ({totalAvailableDays} days)
              </Button>
            </div>
          </div>
        </div>

        {/* Summary of all availability */}
        {totalAvailableDays > 0 && (
          <div className="mt-6 pt-4 border-t">
            <Label className="text-sm font-medium mb-3 block">Availability Summary:</Label>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {Object.entries(dailySlots).map(([date, slots]) => (
                <div key={date} className="flex items-center justify-between text-sm">
                  <span className="font-medium">{new Date(date).toDateString()}</span>
                  <span className="text-gray-600">
                    {slots.length} slot{slots.length !== 1 ? 's' : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};